import React from "react";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/site.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/container.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/header.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/image.css";
import "@ta-interaktiv/semantic-ui/semantic/dist/components/list.css";
import "./styles.scss";
import { Masthead } from "@ta-interaktiv/react-masthead";
import { Footer } from "@ta-interaktiv/react-footer";
import { ThemeSwitch } from "@ta-interaktiv/react-theme-switch";
import artists from "./artists.yml";
import { ArtistBlock } from "components/artistBlock";
import { Cover } from "components/cover";
import Scrollchor from "react-scrollchor";
import NumberFormat from "react-number-format";
import { request } from "http";
import { parseHostname } from "@ta-interaktiv/parse-hostname";

const ARTICLE_ID = "14658681";

export function AppComponent() {
  return (
    <div className="index">
      <Masthead
        hashtags={["Suisse", "Musique", "youtube", "rewind2019"]}
        articleId={ARTICLE_ID}
      />
      <ThemeSwitch />

      <Cover />

      <div className="ui vertical segment">
        <div className="ui text container">
          <p>
            Lorsqu’on évoque YouTube, on pense d’abord au géant du streaming
            vidéo. Pourtant, la plateforme est également le roi du streaming
            audio, loin devant Spotify et Apple Music en nombre d’utilisateurs.
          </p>
          <p>
            YouTube représente à lui seul 46% de toute la musique écoutée en
            ligne en 2017. Grâce aux données géocodées de YouTube, nous avons
            cartographié la popularité des 20 artistes les plus écoutés en
            Romandie en 2019 (
            <Scrollchor to="methodology" disableHistory>
              <i className="small double angle down icon" />
              En savoir plus sur la méthode
            </Scrollchor>
            ).
          </p>
          <p>
            Chacune des cartes ci-dessous montre la popularité relative de
            l’artiste dans différentes régions et localités du pays. Les zones
            les plus claires ne signifient pas qu’un artiste n’a pas été
            regardé, mais que les utilisateurs étaient plus enclins à écouter
            d’autres musiques.
          </p>
          <Scrollchor to="analysis" disableHistory>
            <i className="small double angle down icon" />
            En fin d'article, retrouvez l’analyse des tendances musicales de
            l’année
          </Scrollchor>
        </div>
        <div className="ui container">
          <div id="top20">
            <h2 className="ui small infographic header">
              Les artistes les plus vus sur YouTube en Suisse romande
            </h2>
            <div className="ui middle aligned selection list top20">
              {artists.map((artist, i) => {
                return (
                  <div className="item" key={"artist-item-" + i}>
                    <img
                      className="ui avatar image"
                      src={require("../../images/portraits/bw/portrait-" +
                        (i + 1) +
                        ".png")}
                    />
                    <div className="content">
                      <Scrollchor to={"artist-" + (i + 1)} disableHistory>
                        <div className="header">{artist.name}</div>
                        <NumberFormat
                          value={artist.count}
                          displayType="text"
                          thousandSeparator="'"
                        />{" "}
                        vues
                      </Scrollchor>
                    </div>
                    <i className="ui icon chevron down" />
                  </div>
                );
              })}
            </div>
            <small className="ui infographic source">
              <b>Source</b>: YouTube
            </small>
          </div>
        </div>
      </div>

      <div
        className="ui center aligned vertical segment"
        style={{ paddingTop: "0px" }}
      >
        <div className="ui compact segment story-teaser">
          <div
            className="story-teaser-image"
            style={{
              backgroundImage: "url(" + require("./teaser_link.jpg") + ")"
            }}
          />
          <a
            href={
              "https://interactif." +
              parseHostname().publicationName +
              ".ch/2019/youtube-playlists"
            }
          >
            Découvrez la <b>playlist</b> des artistes les plus écoutés sur
            YouTube dans votre commune&nbsp;
            <i className="external alternate icon" />
          </a>
        </div>
      </div>

      {artists.map((artist, i) => {
        return (
          <ArtistBlock
            id={i + 1}
            key={"artist-" + (i + 1)}
            name={artist.name}
            language={artist.language}
            sex={artist.sex}
            genre={artist.genre}
            description={artist.description}
            clipId={artist.clipId}
            clipCount={artist.clipCount}
            clipName={artist.clipName}
            count={artist.count}
            municipalities={artist.municipalities}
            colors={artist.colors}
          />
        );
      })}

      <div className="ui vertical segment" id="analysis">
        <div className="ui text container">
          <h4 className="ui header">L’analyse des tendances 2019</h4>
          <p>
            La remarque liminaire est sociétale: depuis quelques années,
            l’irruption des plateformes de streaming, de Spotify à Deezer en
            passant par Apple Music, a révolutionné la consommation musicale:
            tout ou presque est désormais rapidement accessible, sur ordinateur
            ou sur téléphone, gratuitement ou payant parfois, souvent bon
            marché. On ne peut plus se fier aux ventes des disques «physiques»,
            que ce soient les CD, les vinyles ou les formats digitaux, pour
            connaître ce qu’écoutent les mélomanes.
          </p>
          <p>
            Les données compilées ici, partant des chiffres en Suisse, commune
            par commune, sur YouTube, donnent ainsi une grille de lecture
            passionnante de ce qu’écoutent «vraiment» les Suisses romands. Et ce
            ne sont ni Johnny Hallyday, ni Renaud, ni Leonard Cohen, par
            exemple, pour citer des artistes dont les récentes sorties
            connaissent le succès dans les échoppes de disques qui perdurent.
          </p>

          <p>
            Est-ce une question d’âge, de segmentation du marché purement
            générationnelle? Les «jeunes» écoutant gratuitement YouTube tandis
            que les «vieux» en resteraient aux CD? Mathieu Jaton, directeur du
            Montreux Jazz Festival, invite à se méfier d’un tel simplisme.
            «Quand on regarde le classement qui apparaît selon vos données, ce
            n’est pas aussi évident. Il y a beaucoup d’artistes très «jeunes»,
            mais d’autres qui sont de grosses pointures pop. Ce que nous
            observons souvent, ce sont des gens qui écoutent les dernières
            sorties ou regardent des clips sur YouTube, puis vont ensuite
            picorer les choses qu’ils apprécient en téléchargeant des playlists
            Spotify. Et quand ils aiment vraiment, ils sont prêts à aller de
            temps en temps acheter un CD ou un vinyle. Il se peut donc que ce
            soit la même personne qui profite des différents supports.»
          </p>

          <b>Sous les radars</b>
          <p>
            Reste que le classement des «vues» sur YouTube en Suisse romande a
            une couleur tendance furieusement à la mode, mais qui passe souvent
            sous les grands radars médiatiques. François Küffer, responsable de
            la programmation musicale de Couleur 3: «Les artistes de ce
            classement ne sont pas diffusés, ou presque pas, sur notre antenne.
            Nous avons un ADN de chaîne, et un mandat de service public, qui
            nous demande de proposer un choix alternatif et qualitatif: si sous
            passions Ninho ou Aya Nakamura, même si j’ai des programmateurs qui
            sifflotent «Djadja» dans les corridors, on se retrouve dans une
            programmation proche des stations commerciales.» Et les auditeurs de
            Couleur 3 râlent: «Ils ont en moyenne 40 ans. Rajeunir l’audience
            est un questionnement constant, et c’est difficile sans mécontenter
            ceux qui nous écoutent déjà.» Pas trop de tout-venant à la mode,
            donc, d’autant que les algorithmes utilisés par YouTube tendent à
            privilégier les grosses pointures pop: «Même à un fan de metal,
            YouTube finit souvent par proposer ça au bout de quelques titres».
          </p>

          <b>Trois tendances</b>
          <p>
            François Küffer voit aussi, de façon plus générale, une explication
            à la faible exposition médiatique <i>mainstream</i> de la plupart
            des artistes du classement YouTube: «Il y a un peu ce que j’appelle
            la «police du rock», les gardiens d’une sorte de bon goût.»
            Programmateurs de chaînes, de clubs ou de festivals, chroniqueurs
            musicaux des journaux, dont celui que vous tenez dans les mains:
            beaucoup se retrouvent souvent en gardiens du temple plutôt qu’en
            dénicheurs de tendance à succès, imposant leur goût, une vision de
            l’histoire. Un artiste indépendant louisianais ou une réédition des
            Beatles ont parfois nettement plus de chances d’avoir droit à un
            article que le dernier «featuring» de Niska ou J. Balvin.
          </p>
          <p>
            Cette liste, que dit-elle? Mathieu Jaton: «On voit clairement se
            dégager trois groupes d’artistes. D’abord la prééminence du
            reggaeton, avec J. Balvin, Ozuna ou Daddy Yankee: ce rap latino est
            depuis deux ans une vraie tendance, autant au niveau international
            qu’en Suisse. On constate aussi que ceux qui sont en tête sont très
            «crossover» dans l’esprit, ont un sens du «featuring», une logique
            de réseau, d’addition des publics: J. Balvin va ainsi collaborer
            avec un artiste de rock ou de rap américain.» Le second groupe
            d’artistes, ce sont les rappeurs français: «Là, on est clairement
            dans l’influence française», poursuit Jaton. Il y a l’exception de
            Capital Bra, venu aux oreilles des Romands via son triomphe en
            Suisse alémanique (au niveau de la Suisse entière, le rappeur venu
            de Berlin est premier sur YouTube en 2019). «Le troisième groupe, ce
            sont les grandes vedettes pop, les incontournables comme Lady Gaga
            ou Ed Sheeran», constate-t-il.
          </p>
          <p>
            Ces classements peuvent-ils influencer la programmation de festivals
            comme Montreux? «Nous cherchons plutôt à attraper les artistes avant
            qu’ils y soient, explique le directeur. Après, ils deviennent vite
            inabordables, ou nous n’arrivons pas à avoir une exclusivité. Il y a
            des exemples, comme Adèle en 2008, PNL, qui était à Montreux en
            2016, ou Jorja Smith il y a deux ans. L’été dernier, Lizzo a été
            programmée juste avant qu’elle n’explose.» Il termine sur une
            pirouette: «J’ai montré le classement à l’un des programmateurs du
            Lab, notre scène Découvertes. Il m’a dit qu’elle était déjà
            dépassée. La tendance du reggaeton et du latino est en train de se
            terminer. L’année prochaine, ce sera celle du Nigeria: un artiste
            comme Burna Boy triomphe déjà dans les clubs.»
          </p>
        </div>
      </div>

      <div className="ui vertical grey segment" id="methodology">
        <div className="ui text container">
          <h4 className="ui header">Méthode</h4>
          <p>
            Nous avons récolté les statistiques de vues des 20 artistes les plus
            regardés sur YouTube (
            <a href="https://charts.youtube.com/location/0x478c64ef6f596d61%3A0x5c56b5110fcb7b15?date_end=2019-12-14T00%3A00%3A00Z">
              <i className="external alternate icon" />
              &nbsp;YouTube Music Charts &amp; Insights
            </a>
            ) du 1er janvier au 3 décembre 2019 pour 999 municipalités, 18
            cantons et l’ensemble de la Suisse. La popularité relative de chaque
            artiste est basée sur son nombre total de vues divisé par le nombre
            total de vues de la municipalité ou du canton durant la même
            période.
          </p>
          <p>
            Si une commune n’existe pas dans les statistiques YouTube, c’est le
            canton sans les communes comptabilisées qui est montré. Le style
            musical de chaque artiste provient de l’API de Spotify.
          </p>
        </div>
      </div>

      <Footer
        questions={[
          "Le contenu était-il informatif?",
          "Les cartes étaient-elles suffisamment claires?",
          "La mise en forme vous plaît-elle?"
        ]}
        mailTo="Duc-Quang.Nguyen@tamedia.ch"
        hidePortfolioTeaser
        locale="fr"
        articleId={ARTICLE_ID}
        credits={[
          ["Cartes et données", "Duc-Quang Nguyen"],
          ["Textes", "Christophe Passer et Duc-Quang Nguyen"],
          ["Conception", "Mathieu Rudaz"],
          [
            "Inspiration",
            "New York Times - <a href='https://www.nytimes.com/interactive/2017/08/07/upshot/music-fandom-maps.html'>What Music Do Americans Love the Most? 50 Detailed Fan Maps</a>"
          ]
        ]}
      />
    </div>
  );
}
